import React, {useEffect, useState} from 'react';
import {useResizeObserver} from "../../../hooks/useResizeObserver";
import {GridPaginationModel} from "@mui/x-data-grid-pro";
import {addToUrlParams} from "@dreebit-registry/vsm-microservice-components";
import {useSearchParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import _ from "lodash";
import {Button} from "@mui/material";
import {AddRounded} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";
import ListFilter from "../../../components/ListFilter";
import SystemAnnouncementsTable from "../../../components/SystemAnnouncementsTable";
import CreateSystemAnnouncementModal from "../../../components/SystemAnnouncementsTable/createSystemAnnouncementModal";
import UpdateSystemAnnouncementModal from "../../../components/SystemAnnouncementsTable/updateSystemAnnouncementModal";
import {ToastMessageListTDAs} from "../../../graphql/queries/toastMessageListTDAs";

const SystemAnnouncementListScreen = () => {
    const {t} = useTranslation();
    const [ref, filterHeight] = useResizeObserver();
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editToastMessageId, setEditToastMessageId] = useState<string | null>(null);
    const [filterFormKey, setFilterFormKey] = useState(0);

    const showEnvBanner = process.env.REACT_APP_SHOW_TESTING_BANNER === "true"
    const tableHeight = viewportHeight
        - filterHeight
        - (showEnvBanner ? 40 : 0) // Test Env Banner
        - 63 // Header
        - 35 // Padding from content

    const filterKeys: string[] = ['status', 'fromDate', 'toDate']

    const [urlParams, setUrlParams] = useSearchParams();

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // The lib's ListFilter drops inputType "datetime" TDAs (its TDAFilterForm
    // only admits select/dateRange/search), so the filter form is rendered
    // through the local TDAForm wrapper instead.
    const filterTDAsQuery = useQuery(ToastMessageListTDAs, {
        variables: {typeDefinitionIndex: "TOAST_MESSAGE_LIST_FILTER"},
    });
    const filterAttributes = _.get(filterTDAsQuery, 'data.toastMessageList.typeDefinitionAttributeList.typeDefinitionAttributes', []) ?? [];

    const initialFilterValues = Object.fromEntries(
        filterKeys.map(key => [key, urlParams.get(key) ?? undefined])
    );

    const writeFilterParams = (values: {[key: string]: any}) => {
        const newParams = new URLSearchParams(urlParams);
        filterKeys.forEach(key => {
            const value = values[key];
            if (value === undefined || value === null || value === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        });
        setUrlParams(newParams, {replace: true});
    };

    return (
        <>
            <div
                ref={ref}
                className={'flex-row'}
                style={{justifyContent: 'space-between', alignItems: 'center', padding: '8px 8px 13px 8px'}}
            >
                <ListFilter
                    key={filterFormKey}
                    attributes={filterAttributes}
                    initialValues={initialFilterValues}
                    onValuesChange={writeFilterParams}
                    onReset={() => {
                        writeFilterParams({});
                        setFilterFormKey((k) => k + 1);
                    }}
                    formStyle={{paddingTop: '0px'}}
                />
                <Button
                    variant="contained"
                    startIcon={<AddRounded/>}
                    onClick={() => setCreateModalOpen(true)}
                >
                    {t('Add')}
                </Button>
            </div>
            <SystemAnnouncementsTable
                height={tableHeight}
                urlParams={urlParams}
                filterKeys={filterKeys}
                onChangePagination={(newPaginationModel: GridPaginationModel) => {
                    if (newPaginationModel) {
                        addToUrlParams(urlParams, newPaginationModel, setUrlParams)
                    }
                }}
                onEditSystemAnnouncement={(id) => setEditToastMessageId(id)}
            />
            {createModalOpen && (
                <CreateSystemAnnouncementModal
                    open={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                />
            )}
            {editToastMessageId && (
                <UpdateSystemAnnouncementModal
                    toastMessageId={editToastMessageId}
                    open={!!editToastMessageId}
                    onClose={() => setEditToastMessageId(null)}
                />
            )}
        </>
    );
};

export default SystemAnnouncementListScreen;
